import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import type { BundledActivationFlowConfig } from 'src/graphql';
import {
  buildPricingExportGrid,
  FIRST_COL_FIELD_NAME,
  type PricingExportColumn,
} from 'src/pages/Settings/memberships/utils/build-pricing-export-grid';
import type { BrandRaw } from 'src/types';
import { useHtmlTemplateExport } from 'src/utils/hooks';

type PricingExportParams = {
  activationFlowConfig: BundledActivationFlowConfig | null;
  segments: BrandRaw[];
  selectedSegmentId: string | null;
};

export const usePricingExport = ({
  activationFlowConfig,
  segments,
  selectedSegmentId,
}: PricingExportParams) => {
  const selectedBrand = useMemo(
    () => segments.find((segment) => String(segment.brandId) === selectedSegmentId),
    [segments, selectedSegmentId],
  );
  const exportGrid = useMemo(
    () => (activationFlowConfig ? buildPricingExportGrid(activationFlowConfig) : null),
    [activationFlowConfig],
  );
  const exportColumns = useMemo(() => {
    if (!exportGrid) {
      return [];
    }

    return exportGrid.columns.map<PricingExportColumn>((column) => ({
      ...column,
      align: column.field === FIRST_COL_FIELD_NAME ? 'left' : 'center',
    }));
  }, [exportGrid]);

  const exportFileName = useMemo(() => {
    const brandName = selectedBrand?.name?.trim() ?? '';
    const normalizedBrand = brandName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    const dateToken = dayjs().format('MMMM_YYYY').toLowerCase();
    const prefix = normalizedBrand ? `${normalizedBrand}_` : '';

    return `${prefix}membership_pricing_${dateToken}`;
  }, [selectedBrand?.name]);

  const { templateRef, templateStyle, isExporting, prepareTemplate, exportTemplate } =
    useHtmlTemplateExport(exportFileName);

  const exportTemplateData = useMemo(
    () => ({
      clientSegment: selectedBrand?.name ?? '',
      columns: exportColumns,
      rows: exportGrid?.rows ?? [],
      logoUrl: selectedBrand?.logo ?? null,
    }),
    [exportColumns, exportGrid?.rows, selectedBrand?.logo, selectedBrand?.name],
  );

  const handleExportClick = useCallback(() => {
    prepareTemplate();
  }, [prepareTemplate]);

  const handleTemplateReady = useMemo(() => exportTemplate, [exportTemplate]);

  return {
    exportGrid,
    exportTemplateData,
    handleExportClick,
    handleTemplateReady,
    isExporting,
    templateRef,
    templateStyle,
  };
};
